import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import DeleteModal from '../../common/DeleteModal';
import PublishActivateDeactivateModal from '../../common/PublishActivateDeactivateModal';
import { MultiSelect } from 'primereact/multiselect';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { convertUTCToLocal } from '../../utils/dateUtils';

const RoleList = ({ persona }) => {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    record: null,
  });
  const [confirmActivateModal, setConfirmActivateModal] = useState({
    open: false,
    record: null,
  });
  const navigate = useNavigate();
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
    filters: {
      type: { value: null, matchMode: FilterMatchMode.IN },
      name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
    },
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        // eslint-disable-next-line no-undef
        `${process.env.REACT_APP_SERVER_URL}/api/roles`,
        {
          withCredentials: true,
          //params: paginationParams,
        }
      );

      const updatedData =
        response?.data?.data?.map((item) => ({
          ...item,
          type: item.is_internal
            ? t('internal')
            : item.is_external
              ? t('external')
              : '',
        })) || [];

      setData(updatedData);
      setTotalRecords(response?.data?.total || 0);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona]);

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
      page: event.page,
    }));
  };

  const columns = [
    { field: 'id', header: t('ID'), sortable: true },
    { field: 'name', header: t('name'), sortable: true, filter: true },
    {
      header: t('type'),
      field: 'type',
      columnProps: {
        showFilterMatchModes: false,
        filter: true,
        filterField: 'type',
        filterElement: (options) => (
          <MultiSelect
            value={options.value}
            options={[
              { label: t('internal'), value: t('internal') },
              { label: t('external'), value: t('external') },
            ]}
            onChange={(e) => {
              options.filterCallback(e.value);
            }}
            placeholder={t('any')}
            style={{ minWidth: '120px' }}
          />
        ),
      },
    },
    {
      field: 'createdAt',
      header: t('Created At'),
      sortable: true,
      body: (rowData) => convertUTCToLocal(rowData.createdAt),
    },
  ];

  const actions = [
    // { text: "View", color: 'primary', onClick: (row) => navigate(`/${persona}/roles/details/${row.id}`) },
    {
      text: 'Edit',
      color: 'secondary',
      onClick: (row) => navigate(`/${persona}/roles/edit/${row.id}`),
    },
    // { text: "Delete", onClick: (row) => setConfirmDeleteModal({ open: true, record: row }), color: "primary", status: 'danger' },
    {
      text: 'Publish / Activate / Deactivate',
      onClick: (row) => setConfirmActivateModal({ open: true, record: row }),
      color: 'primary',
      status: 'success',
    },
  ];

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={totalRecords}
        onDataTableEvent={onDataTableEvent}
        enableCreate={true}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
        actions={actions}
        sectionName={'Roles'}
      />

      {confirmDeleteModal && (
        <DeleteModal
          sentData={confirmDeleteModal.record}
          onEvent={setConfirmDeleteModal}
          onConfirm={fetchRoles}
          apiPath="roles"
          axios={axiosInstance}
          columnName="name"
        />
      )}

      {confirmActivateModal.open && (
        <PublishActivateDeactivateModal
          sentData={confirmActivateModal.record}
          onEvent={setConfirmActivateModal}
          onConfirm={fetchRoles}
          apiPath="roles"
          axios={axiosInstance}
          columnName="name"
          dbAction="status"
          sectionName={'Roles'}
        />
      )}
    </div>
  );
};

export default RoleList;
